/**
 * FileManager Constants
 *
 * Feature-specific constants and configuration values.
 */

/**
 * Special folder ID representing the root level
 */
export const ROOT_FOLDER_ID = "root";

/**
 * Stale times for React Query cache (in milliseconds)
 */
export const FILE_MANAGER_STALE_TIMES = {
  /** Stale time for folder list queries */
  FOLDERS: 30 * 1000, // 30 seconds
  /** Stale time for folder details (contents) */
  FOLDER_DETAILS: 10 * 1000, // 10 seconds
  /** Stale time for entries list */
  ENTRIES: 10 * 1000, // 10 seconds
  /** Stale time for search results */
  SEARCH: 60 * 1000, // 1 minute
} as const;

/**
 * Garbage collection times for React Query cache (in milliseconds)
 */
export const FILE_MANAGER_GC_TIMES = {
  /** Default GC time for most queries */
  DEFAULT: 2 * 60 * 1000, // 2 minutes
  /** GC time for search results */
  SEARCH: 5 * 60 * 1000, // 5 minutes
} as const;

/**
 * Default folder name for creation
 */
export const DEFAULT_FOLDER_NAME = "Nouveau dossier";

/**
 * Toast messages (French)
 */
export const FILE_MANAGER_MESSAGES = {
  // Folder messages
  FOLDER_CREATED: "Dossier créé avec succès",
  FOLDER_UPDATED: "Dossier mis à jour avec succès",
  FOLDER_DELETED: "Dossier supprimé avec succès",
  FOLDER_CREATE_ERROR: "Erreur lors de la création du dossier",
  FOLDER_UPDATE_ERROR: "Erreur lors de la mise à jour du dossier",
  FOLDER_DELETE_ERROR: "Erreur lors de la suppression du dossier",
  FOLDER_RENAMED: "Dossier renommé avec succès",
  FOLDER_RENAME_ERROR: "Erreur lors du renommage du dossier",

  // Entry messages
  ENTRY_CREATED: "Fichier créé avec succès",
  ENTRY_UPDATED: "Fichier mis à jour avec succès",
  ENTRY_DELETED: "Fichier supprimé avec succès",
  ENTRY_RENAMED: "Fichier renommé avec succès",
  ENTRY_CREATE_ERROR: "Erreur lors de la création du fichier",
  ENTRY_UPDATE_ERROR: "Erreur lors de la mise à jour du fichier",
  ENTRY_DELETE_ERROR: "Erreur lors de la suppression du fichier",
  ENTRY_RENAME_ERROR: "Erreur lors du renommage du fichier",

  // Upload messages
  UPLOAD_SUCCESS: (filename: string) => `Fichier "${filename}" téléchargé avec succès`,
  UPLOAD_MULTIPLE_SUCCESS: (count: number) =>
    `${count} fichier${count > 1 ? "s" : ""} téléchargé${count > 1 ? "s" : ""} avec succès`,
  UPLOAD_ERROR: "Erreur lors du téléchargement du fichier",
  UPLOAD_MULTIPLE_ERROR: "Erreur lors du téléchargement des fichiers",

  // Bulk operations
  BULK_DELETE_SUCCESS: (count: number) =>
    `${count} élément${count > 1 ? "s" : ""} supprimé${count > 1 ? "s" : ""} avec succès`,
  BULK_DELETE_ERROR: "Erreur lors de la suppression de certains éléments",

  // Move messages
  FOLDER_MOVE_ERROR: "Erreur lors du déplacement du dossier",
  ENTRY_MOVE_ERROR: "Erreur lors du déplacement du fichier",
  MOVE_MULTIPLE_SUCCESS: (count: number, folderName: string) => `${count} éléments déplacés dans «\u00a0${folderName}\u00a0»`,
  MOVE_SUCCESS: (itemName: string, folderName: string) => `«\u00a0${itemName}\u00a0» déplacé dans «\u00a0${folderName}\u00a0»`,

  // Move restriction errors
  FOLDER_MOVE_RESTRICTION_ERROR_TITLE: "Déplacement impossible",
  FOLDER_MOVE_RESTRICTION_ERROR_DESCRIPTION: "Ce dossier ne peut pas être déplacé vers cet emplacement car ses restrictions d'accès sont incompatibles avec le dossier de destination.",
  FOLDER_MOVE_RESTRICTION_ERROR_BUTTON: "Fermer",

  // Copy link messages
  LINK_COPIED: "Lien copié dans le presse-papier",
  LINK_COPY_ERROR: "Erreur lors de la copie du lien",

  // Drag & drop messages
  UPLOAD_IN_PROGRESS: "Un téléchargement est déjà en cours",
  UPLOAD_INLINE: (fileName?: string) => (fileName ? `Import en cours\u00a0: ${fileName}…` : "Import en cours…"),
  MOVE_HINT: "Pour déplacer un fichier ou un dossier, glissez-le sur un dossier de la liste ou du chemin ci-dessus, ou choisissez « Déplacer vers… » dans son menu.",
  BREADCRUMB_DROP_HINT: "Déposez sur un dossier du chemin pour l'y déplacer",
  DROP_HINT: "Astuce : glissez-déposez des fichiers depuis votre ordinateur pour les importer directement.",
  DROP_OVERLAY_TEXT: "Déposez vos fichiers ici pour les importer",
} as const;

/**
 * Default pagination settings
 */
export const FILE_MANAGER_PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PER_PAGE: 50,
} as const;
